/**
 * Rules for the notification bell badge and ping animation.
 *
 * "Connected" vs "attended": opening the panel or aging out stops urgency;
 * stale unread rows are auto-marked read on load so old DB state does not linger.
 */

/** Unread older than this no longer counts toward badge or ping. */
export const NOTIFICATION_STALE_DAYS = 7;
export const NOTIFICATION_STALE_MS = NOTIFICATION_STALE_DAYS * 24 * 60 * 60 * 1000;

/** Unread newer than this (and still actionable) get the ping animation. */
export const NOTIFICATION_PING_MAX_AGE_MS = 48 * 60 * 60 * 1000;

/** Session toast echoes older than this are ignored for badge counts. */
export const UI_NOTIFICATION_MAX_AGE_MS = 60 * 60 * 1000;

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface BadgeableNotification {
  id: string;
  read?: boolean;
  timestamp: Date;
  priority?: NotificationPriority;
}

function notificationAgeMs(timestamp: Date): number {
  return Date.now() - new Date(timestamp).getTime();
}

export function isNotificationStale(timestamp: Date): boolean {
  return notificationAgeMs(timestamp) > NOTIFICATION_STALE_MS;
}

/** Unread and still within the attention window — counts toward badge number. */
export function isActionableUnread(n: BadgeableNotification): boolean {
  if (n.read) return false;
  return !isNotificationStale(n.timestamp);
}

/** High-priority unread stays ping-eligible until stale; normal unread pings for 48h. */
export function shouldPingNotification(n: BadgeableNotification): boolean {
  if (!isActionableUnread(n)) return false;
  if (n.priority === 'urgent' || n.priority === 'high') return true;
  return notificationAgeMs(n.timestamp) <= NOTIFICATION_PING_MAX_AGE_MS;
}

export function countActionableUnread(notifications: BadgeableNotification[]): number {
  return notifications.filter(isActionableUnread).length;
}

export function shouldShowNotificationPing(notifications: BadgeableNotification[]): boolean {
  return notifications.some(shouldPingNotification);
}

/** UI-store toasts should not inflate the badge after they've aged out. */
export function isUiNotificationBadgeEligible(timestamp: Date): boolean {
  return notificationAgeMs(timestamp) <= UI_NOTIFICATION_MAX_AGE_MS;
}

export interface TaskNotificationCandidate {
  id: string;
  status: string;
  closesAt?: Date;
  participantIds: string[];
  responses: Array<{ participantId: string }>;
}

/** Open assignment with no response — excludes expired and dismissed tasks. */
export function isTaskPendingNotification(
  task: TaskNotificationCandidate,
  userId: string,
  dismissedTaskIds: Set<string>,
): boolean {
  if (!userId || !task.participantIds.includes(userId)) return false;
  if (task.status !== 'open') return false;
  if (task.closesAt && new Date(task.closesAt).getTime() < Date.now()) return false;
  if (task.responses.some((r) => r.participantId === userId)) return false;
  if (dismissedTaskIds.has(task.id)) return false;
  return true;
}
